/**
 * Section « Abonnement » — pénétration et rétention par cohorte.
 *
 * ── Deux natures de données sur la même page, et il faut les distinguer ─────
 *
 * La courbe de pénétration est **mesurée** : elle sort de `daily_snapshots`,
 * un relevé quotidien. Elle est historiquement juste, et irremplaçable — la
 * prod ne conserve aucun historique d'abonnement.
 *
 * Tout le reste est **reconstitué** depuis les dates d'abonnement de la
 * projection (migration 001). C'est rétroactif — la donnée existait déjà en
 * prod — mais approximatif sur un point : `subscriptions_stripe` n'a qu'une
 * ligne par compte, écrasée. Un compte qui s'abonne, résilie, puis se réabonne
 * n'expose que sa date d'origine et son état courant.
 *
 * ── Deux notions de cohorte, à ne pas confondre ─────────────────────────────
 *   · cohorte d'INSCRIPTION → « combien de ces comptes se sont abonnés ? »
 *   · cohorte d'ABONNEMENT  → « combien de ces abonnés le sont restés ? »
 *
 * La première mélange les âges d'abonnement, la seconde les normalise. Les deux
 * sont utiles, elles ne répondent pas à la même question.
 */

import { EngagementHistogram } from '../components/EngagementHistogram';
import { PenetrationChart } from '../components/PenetrationChart';
import { SectionShell } from '../components/SectionShell';
import { SignupCohortChart } from '../components/SignupCohortChart';
import { StatTile } from '../components/StatTile';
import { SubscriptionCohortChart } from '../components/SubscriptionCohortChart';
import { useAuth } from '../hooks/useAuth';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { useSubscriptionStats } from '../hooks/usePeriodStats';
import { formatInteger, formatPercent } from '../lib/format';

export function SubscriptionPage() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();

  const { data, error, isPending, isFetching } = useSubscriptionStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
  });

  const summary = data?.summary;

  return (
    <SectionShell
      lastSyncAt={data?.meta.lastSyncAt}
      isPending={isPending}
      isFetching={isFetching}
      error={error}
      hasData={Boolean(data)}
    >
      <div className="kpi-row">
        <StatTile
          label="Pénétration actuelle"
          value={formatPercent(summary?.currentPenetrationPercent ?? 0)}
          hint={`${formatInteger(summary?.totalSubscribers ?? 0)} abonnés sur ${formatInteger(summary?.totalAccounts ?? 0)} comptes`}
        />
        <StatTile
          label="Conversion de la cohorte"
          value={formatPercent(summary?.conversionPercent ?? 0)}
          hint={`${formatInteger(summary?.convertedUsers ?? 0)} des ${formatInteger(summary?.cohortAccounts ?? 0)} comptes inscrits`}
        />
        <StatTile
          label="Toujours abonnés"
          value={
            summary?.subscriberRetentionPercent != null
              ? formatPercent(summary.subscriberRetentionPercent)
              : '—'
          }
          hint="parmi ceux qui se sont abonnés"
        />
        <StatTile
          label="Délai de conversion"
          value={
            summary?.medianDaysToConvert != null
              ? `${formatInteger(summary.medianDaysToConvert)} j`
              : '—'
          }
          hint="médiane entre inscription et abonnement"
        />
        <StatTile
          label="Premium sans ligne Stripe"
          value={formatInteger(summary?.premiumWithoutSubscriptionRow ?? 0)}
          hint="abonnements attribués à la main"
        />
      </div>

      <div className="chart-grid">
        <PenetrationChart data={data?.penetration ?? []} />

        <SignupCohortChart data={data?.signupCohorts ?? []} />

        <EngagementHistogram
          data={data?.daysToConvertHistogram ?? []}
          title="Délai avant abonnement"
          subtitle="Jours entre la création du compte et le premier abonnement"
        />

        <SubscriptionCohortChart data={data?.subscriptionCohorts ?? []} />
      </div>

      <p className="footnote">
        <strong>La courbe de pénétration est mesurée</strong> (snapshots quotidiens) ; tout le
        reste est <strong>reconstitué</strong> depuis les dates d'abonnement remontées par le sync.
        C'est rétroactif, mais approximatif sur un point : la prod ne garde qu'une ligne
        d'abonnement par compte, écrasée à chaque changement — un compte qui s'abonne, résilie,
        puis se réabonne n'expose que sa date d'origine et son état courant, et les interruptions
        sont invisibles. Deux notions de cohorte cohabitent ici : par mois d'inscription
        (« combien se sont abonnés ? ») et par mois d'abonnement (« combien l'ont été durablement ? »).
        Comptes de test (1 à 4) exclus.
      </p>
    </SectionShell>
  );
}
